import React, { memo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchData } from '@store/metricLens/historicalRangeData/actions';
import EmptyPlaceholder from '@components/common/EmptyPlaceholder';
import { draggingAtom, columnsSelector, historicalRangeDataSelector } from './utils';
import useTableExport from './hooks/useTableExport';
import { StyledTableWrapper, StyledTable, HoverDiv } from './styled';
import {
  sortableContainer,
  sortableElement,
  sortableHandle,
} from 'react-sortable-hoc/dist/react-sortable-hoc';
import { useAtom } from 'jotai';
import { getInvertedPercentageMetricByKey } from '@src/utils/metrics/metricHelpers';
import DragHandle from '@src/components/common/DnD/DragHandle';
import { setSelectedMetrics } from '@src/store/metricLens/base/actions';
import { selectedMetricsSelector } from '@src/store/metricLens/base/selectors';

const DraggableContainer = sortableContainer(({ children }) => children);
const DraggableElement = sortableElement(({ children }) => children);
const DraggableHandle = sortableHandle(({ children }) => children);

export const Col = ({ index, children, ...rest }) => {
  if (index === 0) {
    return (
      <div {...rest}>
        <div style={{ display: 'block', width: '100%', paddingTop: '18px' }}>{children}</div>
      </div>
    );
  }
  return (
    <DraggableElement index={index}>
      <div {...rest}>
        <HoverDiv>
          <div style={{ display: 'block', width: '100%' }}>
            <DraggableHandle>
              <div
                className="handle"
                style={{
                  textAlign: 'center',
                  fontSize: '20px',
                  height: '20px',
                  lineHeight: '20px',
                  cursor: 'pointer',
                  position: 'relative',
                  top: '-6px',
                }}
              >
                <DragHandle></DragHandle>
              </div>
            </DraggableHandle>
            {children}
          </div>
        </HoverDiv>
      </div>
    </DraggableElement>
  );
};

const columnKey = 'dataKey';

/**
 * @param metricName may be primary metric name or percentage metric name
 */
function findIndexInSelectedMetrics(selectedMetrics: string[], metricName: string) {
  let index = selectedMetrics.indexOf(metricName);
  if (index === -1) {
    const primaryMetric = getInvertedPercentageMetricByKey({ metricKey: metricName });
    index = selectedMetrics.indexOf(primaryMetric);
  }
  return index;
}

function MetricTable() {
  const dispatch = useDispatch();
  const historicalData = useSelector(historicalRangeDataSelector);
  const selectedMetrics = useSelector(selectedMetricsSelector);
  const columns = useSelector(columnsSelector);
  const [, setDragging] = useAtom(draggingAtom);

  useTableExport();

  useEffect(() => {
    setDragging(false);
    dispatch(fetchData());
  }, []);

  const headerCellProps = (args) => ({
    tagName: Col,
    index: args.columnIndex,
  });

  const handleSortEnd = ({ oldIndex, newIndex }) => {
    setDragging(false);
    if (oldIndex === newIndex) {
      return;
    }
    const fromIndex = findIndexInSelectedMetrics(selectedMetrics, columns[oldIndex]?.[columnKey]);
    const toIndex = findIndexInSelectedMetrics(selectedMetrics, columns[newIndex]?.[columnKey]);
    if (fromIndex !== -1 && toIndex !== -1) {
      const newSequence = [...selectedMetrics];
      [newSequence[toIndex], newSequence[fromIndex]] = [
        newSequence[fromIndex],
        newSequence[toIndex],
      ];
      dispatch(setSelectedMetrics(newSequence));
    }
  };

  return (
    <StyledTableWrapper>
      <DraggableContainer
        useDragHandle
        onSortEnd={handleSortEnd}
        axis="x"
        onSortStart={() => {
          setDragging(true);
        }}
      >
        <StyledTable
          fixed
          data={historicalData}
          columns={columns}
          rowHeight={110}
          headerHeight={100}
          headerCellProps={headerCellProps}
          emptyRenderer={<EmptyPlaceholder className="" emptyPlaceholder="No Data" />}
        />
      </DraggableContainer>
    </StyledTableWrapper>
  );
}

export default memo(MetricTable);
